import { useEffect, useState } from 'react'

import { Popup } from '@skytech/ui'
import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon'
import AlertOctagramOutlineIcon from 'mdi-react/AlertOctagramOutlineIcon'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import CheckIcon from 'mdi-react/CheckIcon'
import ClockTimeNineOutlineIcon from 'mdi-react/ClockTimeNineOutlineIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import FileOutlineIcon from 'mdi-react/FileOutlineIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { useDispatch, useSelector } from 'react-redux'

import {
  API_ROUTE_CREDIT_REQUEST_QUOTE_RESET,
  API_ROUTE_CREDIT_REQUEST_QUOTES,
} from '@/api/apiRoutes'
import creaditRequestResources from '@/api/creditRequest'
import Page from '@/components/content/Page'
import Button from '@/components/form/Button'
import Input from '@/components/form/Input'
import { Translation } from '@/components/i18n/Translation'
import ButtonLink from '@/components/util/ButtonLink'
import Highlight from '@/components/util/Highlight'
import LoaderCircle from '@/components/util/LoaderCircle'
import NoResults from '@/components/util/NoResults'
import { getQuotes, setQuotes } from '@/ducks/creditRequestQuotesReducer'
import { useMountEffect } from '@/hooks/mounting-hooks'
import useRenderLimit from '@/hooks/render-limit-hook'
import { useLocalDate } from '@/hooks/use-local-date'
import { useLocalDateTime } from '@/hooks/use-local-date-time'
import { useApiCallerGET, useApiCallerPOST } from '@/hooks/useApiCaller2'
import { MIN, SEC } from '@/util/formatters/dates'
import { numberFormat } from '@/util/formatters/numbers'
import { applyTextFilter, appRoutes } from '@/util/misc'

import './credit-request-page.scss'

dayjs.extend(localizedFormat)
// @ts-ignore
const stringMatcher = c =>
  `${c.customer_name} ${c.customer_address_line1} ${c.customer_address_line2} ${c.customer_address_city} ${c.customer_address_zip} ${c.customer_org_no} ${c.currency} ${c.status}`

const CREDIT_REQUEST_STATUS = {
  PENDING: 'PENDING',
  ERROR: 'ERROR',
  DOC_REQUIRED: 'DOC_REQUIRED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN',
}

const IconFromStatus = ({ status }: any) => {
  switch (status) {
    case CREDIT_REQUEST_STATUS.APPROVED:
      return <CheckIcon />
    case CREDIT_REQUEST_STATUS.REJECTED:
      return <CloseIcon />
    case CREDIT_REQUEST_STATUS.ERROR:
      return <AlertOctagramOutlineIcon />
    case CREDIT_REQUEST_STATUS.DOC_REQUIRED:
      return <FileOutlineIcon />
    case CREDIT_REQUEST_STATUS.WITHDRAWN:
      return <ArrowLeftIcon />
    case CREDIT_REQUEST_STATUS.PENDING:
    default:
      return <ClockTimeNineOutlineIcon />
  }
}

const CreditRequestPage = () => {
  const dispatch = useDispatch()

  const quotes = useSelector(getQuotes)

  const [query, setQuery] = useState('')
  const [isLoaded, setIsLoaded] = useState(false)
  const quotesFiltered = applyTextFilter(quotes, query, stringMatcher)

  const { visibleList, ListLoaderComponent } = useRenderLimit(quotesFiltered)

  // @ts-ignore
  const { callApi: getQuotesApi, isLoading } = useApiCallerGET(API_ROUTE_CREDIT_REQUEST_QUOTES)

  const reload = async () => {
    const quotes = await getQuotesApi()
    dispatch(setQuotes(quotes))
    setIsLoaded(true)
  }

  useMountEffect(() => {
    reload().then()
  })

  useEffect(() => {
    const timeout = setInterval(() => {
      reload().then()
    }, 10 * SEC)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <Page className="credit-request-page">
      <h1>
        <Translation id="Quotes" />
      </h1>

      <ButtonLink to={appRoutes.creditRequestNewQuote}>
        <PlusIcon />
        <Translation id="New quote" />
      </ButtonLink>

      <Input onChange={e => setQuery(e.target.value)} placeholder="filter" />

      {isLoading && quotes.length === 0 && !isLoaded ? (
        <LoaderCircle />
      ) : (
        <>
          {visibleList.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>created/updated</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th className="number">Value/Duration</th>
                  <th>NN/QN</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {/* @ts-ignore */}
                {quotesFiltered.map(quote => (
                  <Quote key={quote.id} query={query} reload={reload} {...quote} />
                ))}
              </tbody>
            </table>
          )}

          {quotesFiltered.length === 0 && <NoResults />}
          <ListLoaderComponent />
        </>
      )}
    </Page>
  )
}

const Quote = ({
  id,
  hpe_credit_application_id,
  created,
  updated,
  customer_name,
  customer_address_line1,
  customer_address_line2,
  customer_address_city,
  customer_address_zip,
  customer_org_no,
  currency,
  sales_value,
  status,
  finance_term,
  additional_info,
  query,
  reload,
  qn,
  nn,
}: any) => {
  const messages = JSON.parse(additional_info) || []
  const [isQuotePopupOpen, setIsQuotePopupOpen] = useState(false)
  const { formatTime } = useLocalDateTime()
  const { formatDate } = useLocalDate()

  return (
    <tr onClick={() => setIsQuotePopupOpen(true)}>
      <td className="created">
        <div>{formatDate({ date: created })}</div>

        {updated && formatDate({ date: new Date() }) === formatDate({ date: updated })
          ? formatTime({ date: updated, showDate: false })
          : formatDate({ date: updated })}
      </td>

      <td className="customer">
        <div className="customer-name">
          <Highlight text={customer_name} pattern={query} />
        </div>

        <div className="customer-org-no">
          <Highlight text={customer_org_no} pattern={query} />
        </div>
      </td>

      <td>
        <address className="customer-address">
          <div className="customer-address__line1">
            <Highlight text={customer_address_line1} pattern={query} />
          </div>

          {customer_address_line2 && (
            <div className="customer-address__line2">
              <Highlight text={customer_address_line2} pattern={query} />
            </div>
          )}

          <div className="customer-address__zip-city">
            <Highlight text={customer_address_zip} pattern={query} />
            , <Highlight text={customer_address_city} pattern={query} />
          </div>
        </address>
      </td>

      <td className="number">
        <div>
          {numberFormat(sales_value)} <Highlight text={currency} pattern={query} />
        </div>

        <div>{numberFormat(finance_term)} months</div>
      </td>

      <td>
        <div>{nn}</div>
        <div className="qn">{qn}</div>
      </td>

      <td className={`status ${status}`}>
        {dayjs().diff(dayjs(created)) / MIN < 2 && status === CREDIT_REQUEST_STATUS.PENDING ? (
          <LoaderCircle />
        ) : (
          <>
            <IconFromStatus status={status} />
            <Highlight text={status} pattern={query} />
          </>
        )}
      </td>

      <td className="message-from-funder">
        {messages?.length > 0 && (
          <button onClick={() => setIsQuotePopupOpen(true)}>
            <AlertCircleOutlineIcon />
          </button>
        )}
      </td>
      {/* @ts-ignore */}
      <Popup isOpen={isQuotePopupOpen} onClose={() => setIsQuotePopupOpen(false)}>
        <PopupContent
          qn={qn}
          nn={nn}
          status={status}
          creditRequestId={id}
          hpe_credit_application_id={hpe_credit_application_id}
          additional_info={additional_info}
          reload={reload}
          onClose={() => setIsQuotePopupOpen(false)}
        />
      </Popup>
    </tr>
  )
}

export default CreditRequestPage

const PopupContent = ({
  creditRequestId,
  hpe_credit_application_id,
  reload,
  additional_info,
  status,
  qn,
  nn,
  onClose,
}: {
  creditRequestId: number
  hpe_credit_application_id: number
  reload: unknown
  additional_info: string
  status: string
  qn: string
  nn: string
  onClose: () => void
}) => {
  const [popupState, setPopupState] = useState({ qn, nn, creditRequestId })
  // @ts-ignore
  const { callApi: retryApi, isLoading } = useApiCallerPOST(
    API_ROUTE_CREDIT_REQUEST_QUOTE_RESET.replace(':credit_request_id', String(creditRequestId))
  )
  const retry = async () => {
    await retryApi()
    // @ts-ignore
    reload()
    onClose()
  }

  const messages = JSON.parse(additional_info) || []

  const qnNnMutation = useMutation(creaditRequestResources.postQnNn, {
    onSuccess() {
      // @ts-ignore
      reload()
      onClose()
    },
  })

  return (
    <div className="credit-request-details">
      <h3>Request #{creditRequestId}</h3>

      {hpe_credit_application_id && (
        <>
          <h4>Funder ref</h4>
          <p>
            <strong>{hpe_credit_application_id}</strong>
          </p>
        </>
      )}

      {messages.length > 0 && (
        <>
          <h4>Message from funder</h4>
          {/* @ts-ignore */}
          {messages.map((message, idx) => (
            <p key={idx}>{message}</p>
          ))}
        </>
      )}

      <Input
        value={popupState.nn}
        label="nn"
        onChange={e => setPopupState({ ...popupState, nn: e.target.value })}
      />

      <Input
        value={popupState.qn}
        label="qn"
        onChange={e => setPopupState({ ...popupState, qn: e.target.value })}
      />

      <div className="quote-footer">
        <Button className="cancel-button" onClick={onClose}>
          <Translation id="Cancel" />
        </Button>

        {!!hpe_credit_application_id && status === CREDIT_REQUEST_STATUS.ERROR && (
          <div className="retry-quote-button">
            <Button disabled={isLoading} onClick={retry}>
              Retry this quote
            </Button>
          </div>
        )}

        <Button onClick={() => qnNnMutation.mutate(popupState)}>
          {qnNnMutation.isLoading ? <Translation id="Updating..." /> : <Translation id="Update" />}
        </Button>
      </div>
    </div>
  )
}
